import { type JSX, useCallback, useMemo } from "react";
import { generatePath, useNavigate } from "react-router";
import { type ColumnDef } from "@tanstack/react-table";
import { SearchUserIllustration } from "vega-illustrations";

import { getUserStatusVariant } from "@vega-api/custom/utils/getUserStatusVariant";
import { type User } from "@vega-api/generated/types/user";
import {
  UserStatus,
  type UserStatus as UserStatusType,
} from "@vega-api/generated/types/userStatus";
import { ACTION_COLUMN_DEFAULT_PROPS } from "@vega-core/components/Table/CoreTable/constants";
import { CoreTable } from "@vega-core/components/Table/CoreTable/CoreTable";
import { Tag } from "@vega-core/components/Tag/Tag";
import { m } from "@vega-core/paraglide/messages";
import {
  DateFormatString,
  formatDateByPattern,
} from "@vega-core/utils/formatDate";
import { convertToTitleCase } from "@vega-core/utils/formatString";
import { isTruthy } from "@vega-core/utils/isTruthy";

import { VegaModulesPaths } from "../../../../paths";

import { InvitationCell } from "./components/InvitationCell";
import { UserCell } from "./components/UserCell";
import { UserMenu } from "./components/UserMenu";

type Props = {
  data: User[];
  isLoading?: boolean;
  paginationElement?: JSX.Element | null;
};

export function isRegisteredUser(userStatus?: UserStatusType): boolean {
  return userStatus === UserStatus.ACTIVE || userStatus === UserStatus.INACTIVE;
}

const useUsersTableColumns = () =>
  useMemo(
    () =>
      [
        {
          id: "nameOrEmail",
          isMobileActionGroup: true,
          cell: ({ row }) => {
            const { firstName, lastName, email, status } = row.original;

            return isRegisteredUser(row.original.status) ? (
              <UserCell
                email={email}
                firstName={firstName}
                lastName={lastName}
                status={status}
              />
            ) : (
              <InvitationCell email={email} />
            );
          },
          header: m.titles_name(),
          sortingFn: (rowA, rowB, columnId) => {
            const valueA = rowA.getValue<string>(columnId).toLowerCase() || "";
            const valueB = rowB.getValue<string>(columnId).toLowerCase() || "";

            return valueA.localeCompare(valueB);
          },
        },
        {
          accessorKey: "lastActivity",
          cell: ({ row, getValue }) => {
            const date = getValue<string>();

            return isRegisteredUser(row.original.status) && isTruthy(date)
              ? formatDateByPattern(date, DateFormatString.US_DATE_WITH_COMMA)
              : "";
          },
          header: m.titles_lastActivity(),
        },
        {
          accessorKey: "status",
          header: m.titles_status(),
          align: "left",
          cell: (info) => {
            const status = info.getValue<UserStatus>();

            return (
              <Tag variant={getUserStatusVariant(status)}>
                {convertToTitleCase(status)}
              </Tag>
            );
          },
        },
        {
          accessorKey: "id",
          cell: ({ row }) => (
            <UserMenu
              email={row.original.email}
              firstName={row.original.firstName}
              lastName={row.original.lastName}
              status={row.original.status}
              userId={row.original.id}
            />
          ),
          ...ACTION_COLUMN_DEFAULT_PROPS,
        },
      ] as ColumnDef<User>[], // TODO Update : replace cast with satisfies or typed array
    [],
  );

export const UsersTable = ({ data, isLoading, paginationElement }: Props) => {
  const navigate = useNavigate();
  const columns = useUsersTableColumns();

  const handleRowClick = useCallback(
    (row: User) =>
      navigate(generatePath(VegaModulesPaths.SettingsUser, { id: row.id })),
    [navigate],
  );

  return (
    <CoreTable
      columns={columns}
      data={data}
      dataTestId="user-management-users-table"
      emptyState={{
        description: m.userManagement_users_noUsers(),
        illustration: <SearchUserIllustration />,
      }}
      isLoading={isLoading}
      onRowClick={handleRowClick}
      paginationElement={paginationElement}
    />
  );
};
